import { _ as _extends } from './extends-a2cb9f0c.esm.js';
import React__default from 'react';
import { StyleSheet, Platform, Pressable, View } from 'react-native';
import { u as useTheme } from './useTheme-90e8e2c2.esm.js';
import { A as AppText } from './index-6b88191c.esm.js';

const generateInitials = fullName => {
  var _initials$shift, _initials$pop;
  let name = fullName;
  let rgx = new RegExp(/(\p{L}{1})\p{L}+/, "gu");
  let initial;
  const initials = [];
  while ((initial = rgx.exec(name)) !== null) {
    initials.push(initial[0]);
  }
  return ((((_initials$shift = initials.shift()) === null || _initials$shift === void 0 ? void 0 : _initials$shift[0]) || "") + (((_initials$pop = initials.pop()) === null || _initials$pop === void 0 ? void 0 : _initials$pop[0]) || "")).toUpperCase();
};
function stringToHslColor(str, s) {
  if (str === "Not Applicable") return "grey";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let h = hash % 360;
  let l;
  if (h > 200 && h < 280 || h > 40 && h < 80) {
    // if hue is in blue or yellow range
    l = 30; // set lightness to dark
  } else {
    l = 70; // set lightness to light
  }

  // Adjust lightness to ensure it's not too dark
  l = Math.max(l, 50);
  return "hsl(".concat(h, ", ").concat(s, "%, ").concat(l, "%)");
}
const getSizeConfig = size => {
  if (typeof size === "number") {
    return {
      avatarSize: size,
      fontSize: size * 0.4
    };
  }
  switch (size) {
    case "small":
      return {
        avatarSize: 28,
        fontSize: 11
      };
    case "large":
      return {
        avatarSize: 52,
        fontSize: 20
      };
    default:
      // medium
      return {
        avatarSize: 36,
        fontSize: 14
      };
  }
};
function generatePremiumColor(str) {
  if (str === "Not Applicable") {
    return {
      bg: "#6B7280",
      text: "#FFFFFF"
    };
  }
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const premiumColors = [{
    bg: "#3B82F6",
    text: "#FFFFFF"
  },
  // Blue
  {
    bg: "#10B981",
    text: "#FFFFFF"
  },
  // Emerald
  {
    bg: "#8B5CF6",
    text: "#FFFFFF"
  },
  // Violet
  {
    bg: "#F59E0B",
    text: "#FFFFFF"
  },
  // Amber
  {
    bg: "#EF4444",
    text: "#FFFFFF"
  },
  // Red
  {
    bg: "#06B6D4",
    text: "#FFFFFF"
  },
  // Cyan
  {
    bg: "#84CC16",
    text: "#FFFFFF"
  },
  // Lime
  {
    bg: "#EC4899",
    text: "#FFFFFF"
  },
  // Pink
  {
    bg: "#6366F1",
    text: "#FFFFFF"
  },
  // Indigo
  {
    bg: "#14B8A6",
    text: "#FFFFFF"
  } // Teal
  ];

  return premiumColors[Math.abs(hash) % premiumColors.length];
}
const AppAvatar = _ref => {
  let {
    style,
    onPress,
    label = "Not Applicable",
    size = "medium",
    labelStyle,
    showBorder = true,
    ...props
  } = _ref;
  const {
    currentTheme
  } = useTheme();
  const config = getSizeConfig(size);
  const colors = generatePremiumColor(label);
  const _style = styleGenerator(currentTheme, config, colors, showBorder);
  const Component = onPress ? Pressable : View;
  return /*#__PURE__*/React__default.createElement(Component, _extends({
    onPress: onPress,
    style: [_style.baseStyle, style, onPress && _style.pressable]
  }, props), /*#__PURE__*/React__default.createElement(AppText, {
    style: [_style.baseLabelStyle, labelStyle],
    wrapperStyle: {
      justifyContent: "center"
    }
  }, generateInitials(label)));
};
const styleGenerator = (theme, config, colors, showBorder) => {
  const {
    avatarSize,
    fontSize
  } = config;
  return StyleSheet.create({
    baseStyle: {
      justifyContent: "center",
      alignItems: "center",
      width: avatarSize,
      height: avatarSize,
      borderRadius: avatarSize / 2,
      backgroundColor: colors.bg,
      shadowColor: "#000",
      shadowOffset: {
        width: 0,
        height: 1
      },
      shadowOpacity: 0.1,
      shadowRadius: 2,
      elevation: 2,
      borderWidth: showBorder ? 1 : 0,
      borderColor: showBorder ? "rgba(255, 255, 255, 0.2)" : "transparent",
      ...Platform.select({
        web: {
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          transition: "transform 0.15s ease, box-shadow 0.15s ease"
        }
      })
    },
    pressable: {
      cursor: Platform.OS === "web" ? "pointer" : "default",
      ...Platform.select({
        web: {
          ":hover": {
            transform: "scale(1.05)",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
          }
        }
      })
    },
    baseLabelStyle: {
      fontFamily: "boldFont",
      fontSize: fontSize,
      color: colors.text,
      fontWeight: "600",
      letterSpacing: 0.5
    }
  });
};
AppAvatar.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "AppAvatar",
  "props": {
    "label": {
      "defaultValue": {
        "value": "\"Not Applicable\"",
        "computed": false
      },
      "required": false,
      "tsType": {
        "name": "string"
      },
      "description": "Full label to convert to initials"
    },
    "size": {
      "defaultValue": {
        "value": "\"medium\"",
        "computed": false
      },
      "required": false,
      "tsType": {
        "name": "union",
        "raw": "\"small\" | \"medium\" | \"large\" | number",
        "elements": [{
          "name": "literal",
          "value": "\"small\""
        }, {
          "name": "literal",
          "value": "\"medium\""
        }, {
          "name": "literal",
          "value": "\"large\""
        }, {
          "name": "number"
        }]
      },
      "description": "Size of the Avatar"
    },
    "showBorder": {
      "defaultValue": {
        "value": "true",
        "computed": false
      },
      "required": false,
      "tsType": {
        "name": "boolean"
      },
      "description": "Whether to show a subtle border"
    },
    "style": {
      "required": false,
      "tsType": {
        "name": "StyleProp",
        "elements": [{
          "name": "ViewStyle"
        }],
        "raw": "StyleProp<ViewStyle>"
      },
      "description": "Style to apply to the Avatar"
    },
    "onPress": {
      "required": false,
      "tsType": {
        "name": "signature",
        "type": "function",
        "raw": "() => void",
        "signature": {
          "arguments": [],
          "return": {
            "name": "void"
          }
        }
      },
      "description": "Event raised when the Avatar is pressed"
    },
    "labelStyle": {
      "required": false,
      "tsType": {
        "name": "StyleProp",
        "elements": [{
          "name": "TextStyle"
        }],
        "raw": "StyleProp<TextStyle>"
      },
      "description": "Style to apply to the label representing the initials"
    }
  }
};

export { AppAvatar as A, stringToHslColor as s };
